import React, { useState, useEffect, useRef } from 'react';

import axios from 'axios';

import Button from 'components/Button/button';
import AnalyseIcon from 'images/icons/combinations/analyseIconBlue.svg';
import EvolutionIcon from 'images/icons/combinations/evolutionIconBlue.svg';
import OperationIcon from 'images/icons/combinations/operationIconBlue.svg';
import RemoveIcon from 'images/icons/signals/removesAndDeletes/subtractWhiteRedBgRounded.svg';

import ConfirmModal from 'components/Dialogs/ConfirmModal/ConfirmModal';
import CommonAlert from 'components/CommonAlert/CommonAlert';
import AddIcon from 'images/icons/signals/addsAndCreates/addBlue.svg';

import Environment from 'utils/environments';

import {
  ModalContent,
  ModalOverlay,
  ModalWrapper,
  ModalHeader,
  ModalHeaderIcon,
  ModalTitle,
  ModalBody,
  FormRow,
  FormLabel,
  Time,
  TimeInputWrapper,
  TimeInputWithIcon,
  TimeInputIcon,
  RadioGroup,
  RadioLabel,
  ModalFooter
} from './style';

function TimeField({ label, name, value, onChange }) {
  const inputRef = useRef();

  const openPicker = () => {
    if (inputRef.current?.showPicker) {
      inputRef.current.showPicker();
    } else {
      inputRef.current?.focus();
    }
  };

  return (
    <Time>
      <span>{label}</span>
      <TimeInputWrapper>
        <TimeInputIcon onClick={openPicker} />
        <TimeInputWithIcon
          ref={inputRef}
          name={name}
          value={value || ''}
          onChange={onChange}
        />
      </TimeInputWrapper>
    </Time>
  );
}

function OperacaoContent({ data, onSave, onClose, restaurantId }) {
  const [form, setForm] = useState({
    productionTimes: []
  });

  const environments = Environment(process.env.REACT_APP_ENV);
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedIndexToRemove, setSelectedIndexToRemove] = useState(null);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('error');

  const openAlert = (severity, message) => {
    setAlertSeverity(severity);
    setAlertMessage(message);
    setShowAlert(true);
  };

  const normalize = (t) => {
    if (!t) return '';
    const [h = '00', m = '00'] = String(t).split(':');
    return `${`${h}`.padStart(2, '0')}:${`${m}`.padStart(2, '0')}`;
  };

  const isEmptyRange = (r) => !normalize(r.start) && !normalize(r.end);

  const hasInvalidRange = (ranges) =>
    ranges.some((r) => {
      const start = normalize(r.start);
      const end = normalize(r.end);
      if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return true;
      return start >= end;
    });

  const hasDuplicateRanges = (ranges) => {
    const seen = new Set();
    for (const r of ranges) {
      const key = `${normalize(r.start)}|${normalize(r.end)}`;
      if (seen.has(key)) return true;
      seen.add(key);
    }
    return false;
  };

  useEffect(() => {
    if (!data) return;

    const mapRange = (item) => ({
      _id: item._id ?? item.id,
      start: item.start || '',
      end: item.end || ''
    });

    if (Array.isArray(data.productionTimeRanges)) {
      const mapped = data.productionTimeRanges.map(mapRange);
      setForm({ ...data, productionTimes: mapped.length ? mapped : [{ start: '', end: '' }] });
      return;
    }

    if (Array.isArray(data.productionTimes)) {
      const mapped = data.productionTimes.map(mapRange);
      setForm({ ...data, productionTimes: mapped.length ? mapped : [{ start: '', end: '' }] });
      return;
    }

    setForm({ ...data, productionTimes: [{ start: '', end: '' }] });
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('productionTimes')) {
      const [, index, field] = name.split('-');
      const updated = [...form.productionTimes];
      updated[index][field] = value;
      setForm({ ...form, productionTimes: updated });
    } else {
      setForm({ ...form, [name]: value });
    }
  };

  const handleAddProductionTime = () => {
    setForm({
      ...form,
      productionTimes: [...form.productionTimes, { start: '', end: '' }]
    });
  };

  const handleSave = () => {
    const all = form.productionTimes || [];

    const filled = all.filter(r => !isEmptyRange(r));

    if (hasInvalidRange(filled)) {
      openAlert('error', 'Formato de hora inválido. O horário inicial deve ser menor que o horário final.');
      return;
    }

    if (hasDuplicateRanges(filled)) {
      openAlert('error', 'Não é permitido adicionar dois horários de produção iguais.');
      return;
    }

    const normalized = filled.map(r => ({
      ...r,
      start: normalize(r.start),
      end: normalize(r.end),
    }));

    onSave({
      ...form,
      productionTimes: normalized,
      productionTimeRanges: normalized,
    });

    onClose();
  };

  const handleRemoveProductionTime = (index) => {
    setSelectedIndexToRemove(index);
    setShowConfirmModal(true);
  };

  const handleRemoveProductionTimeConfirmed = async () => {
    const range = form.productionTimes[selectedIndexToRemove];
    const nextLocal = form.productionTimes.filter((_, i) => i !== selectedIndexToRemove);

    const id = range?._id ?? range?.id;
    if (!id) {
      setForm({ ...form, productionTimes: nextLocal });
      setShowConfirmModal(false);
      openAlert('success', 'Horário removido com sucesso!');
      return;
    }

    try {
      const url = `${environments.restaurants}/parameters/alerts/${restaurantId}/productionRange/${id}`;
      const { data: resp } = await axios.delete(url);

      const rangesFromApi =
        resp?.alerts?.operations?.productionTimeRanges ??
        resp?.productionTimeRanges ??
        [];

      const updatedFromApi = rangesFromApi.map((item) => ({
        _id: item._id ?? item.id,
        start: item.start || '',
        end: item.end || ''
      }));

      setForm((curr) => ({ ...curr, productionTimes: updatedFromApi }));
      onSave({
        ...form,
        productionTimes: updatedFromApi,
        productionTimeRanges: rangesFromApi
      });

      setShowConfirmModal(false);
      openAlert('success', 'Horário removido com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar intervalo de produção:', error);
      setForm({ ...form, productionTimes: nextLocal });
      setShowConfirmModal(false);
      openAlert('error', 'Erro ao remover horário. Tente novamente.');
    }
  };

  return (
    <ModalContent>
      <ModalHeaderIcon>
        <img src={OperationIcon} alt="Operação" width={45} height={39} />
        <ModalTitle>OPERAÇÃO</ModalTitle>
      </ModalHeaderIcon>

      <ModalBody>
        <FormRow isOperation>
          <FormLabel>Recebimento:</FormLabel>
          <RadioGroup>
            <RadioLabel>
              <input
                type="radio"
                name="receptionType"
                value="nf_date"
                checked={form.receptionType === 'nf_date'}
                onChange={handleChange}
              />
              Data da integração da NF
            </RadioLabel>
            <RadioLabel>
              <input
                type="radio"
                name="receptionType"
                value="nf_date_plus_one"
                checked={form.receptionType === 'nf_date_plus_one'}
                onChange={handleChange}
              />
              Até 1 dia da integração da NF
            </RadioLabel>
          </RadioGroup>
        </FormRow>

        <FormRow  isOperation>
          <FormLabel>Copiloto:</FormLabel>
          <TimeField
            label="Horário Inicial"
            name="copilotTimeStart"
            value={form.copilotTimeStart}
            onChange={handleChange}
          />
          <TimeField
            label="Horário Final"
            name="copilotTimeEnd"
            value={form.copilotTimeEnd}
            onChange={handleChange}
          />
        </FormRow>

        <FormRow isOperation>
          <FormLabel>Produção:</FormLabel>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            {form.productionTimes?.map((time, index) => (
              <div
                key={index}
                style={{ display: 'flex', alignItems: 'end', gap: 8, width: '100%', paddingLeft: 10 }}
              >
                <div style={{ display: 'flex', gap: 15, flex: 1 }}>
                  <TimeField
                    label="Horário Inicial"
                    name={`productionTimes-${index}-start`}
                    value={time.start}
                    onChange={handleChange}
                  />
                  <TimeField
                    label="Horário Final"
                    name={`productionTimes-${index}-end`}
                    value={time.end}
                    onChange={handleChange}
                  />
                </div>

                {index < form.productionTimes.length - 1 ? (
                  <img
                    src={RemoveIcon}
                    alt="Remover horário"
                    width={24}
                    height={24}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                    onClick={() => handleRemoveProductionTime(index)}
                  />
                ) : (
                  <img
                    src={AddIcon}
                    alt="Adicionar horário"
                    width={24}
                    height={24}
                    style={{ cursor: 'pointer', flexShrink: 0 }}
                    onClick={handleAddProductionTime}
                  />
                )}
              </div>
            ))}
          </div>
        </FormRow>

        <FormRow  isOperation>
          <FormLabel>Contagem:</FormLabel>
          <div>
            <div style={{ display: 'flex', gap: 15 }}>
              <TimeField
                label="Horário Inicial"
                name="countingTimeStart"
                value={form.countingTimeStart}
                onChange={handleChange}
              />
              <TimeField
                label="Horário Final"
                name="countingTimeEnd"
                value={form.countingTimeEnd}
                onChange={handleChange}
              />
            </div>

            <div className="pt-2">
              <RadioLabel>
                <input
                  type="radio"
                  name="countDateType"
                  value="yesterday"
                  checked={form.countDateType === 'yesterday'}
                  onChange={handleChange}
                />
                Contagem de Ontem
              </RadioLabel>

              <RadioLabel>
                <input
                  type="radio"
                  name="countDateType"
                  value="today"
                  checked={form.countDateType === 'today'}
                  onChange={handleChange}
                />
                Contagem de Hoje
              </RadioLabel>
            </div>
          </div>
        </FormRow>

        <ConfirmModal
          open={showConfirmModal}
          title="Confirmar Remoção"
          content="Você tem certeza que deseja remover esse horário?"
          confirmText="Remover"
          cancelText="Cancelar"
          onConfirm={handleRemoveProductionTimeConfirmed}
          onClose={() => setShowConfirmModal(false)}
          onCancel={() => setShowConfirmModal(false)}
          disableBackDrop={false}
          disableSubmitButton={false}
        />

        <CommonAlert
          open={showAlert}
          autoHideDuration={6000}
          onClose={() => setShowAlert(false)}
          messagePersonal={alertMessage}
          severity={alertSeverity}
          customClassName={alertSeverity === 'success' ? 'alertSuccess' : 'alertError'}
        />
      </ModalBody>

      <ModalFooter>
        <Button
          variant="contained"
          design="contained"
          label="Cancelar"
          className="defaultButton backButton"
          onClick={onClose}
        />
        <Button
          variant="contained"
          design="contained"
          label="Salvar"
          className="defaultButton submitButton"
          onClick={handleSave}
        />
      </ModalFooter>
    </ModalContent>
  );
}

function AnaliseContent({ data, onSave, onClose }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    setForm(data || {});
  }, [data]);

  const handleChange = (e) => {
    const updatedForm = { ...form, [e.target.name]: e.target.value };
    setForm(updatedForm);
  };

  const handleSave = () => {
    onSave(form);
    onClose();
  };

  return (
    <ModalContent>
      <ModalHeaderIcon>
        <img src={AnalyseIcon} alt="Análise" width={45} height={39} />
        <ModalTitle>SUA ANÁLISE</ModalTitle>
      </ModalHeaderIcon>
      <ModalBody>
        <FormRow>
          <FormLabel>Produção:</FormLabel>
          <TimeField
            label="Horário Inicial"
            name="productionTimeStart"
            value={form.productionTimeStart}
            onChange={handleChange}
          />
          <TimeField
            label="Horário Final"
            name="productionTimeEnd"
            value={form.productionTimeEnd}
            onChange={handleChange}
          />
        </FormRow>
        <FormRow>
          <FormLabel>Contagem:</FormLabel>
          <TimeField
            label="Horário Inicial"
            name="countingTimeStart"
            value={form.countingTimeStart}
            onChange={handleChange}
          />
          <TimeField
            label="Horário Final"
            name="countingTimeEnd"
            value={form.countingTimeEnd}
            onChange={handleChange}
          />
        </FormRow>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="contained"
          design="contained"
          label="Cancelar"
          className="defaultButton backButton"
          onClick={onClose}
        />

        <Button
          variant="contained"
          design="contained"
          label="Salvar"
          className="defaultButton submitButton"
          onClick={handleSave}
        />
      </ModalFooter>
    </ModalContent>
  );
}

function EvolucaoContent({ data, onSave, onClose }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    setForm(data || {});
  }, [data]);

  const handleChange = (e) => {
    const updatedForm = { ...form, [e.target.name]: e.target.value };
    setForm(updatedForm);
  };

  const handleSave = () => {
    onSave(form);
    onClose();
  };

  return (
    <ModalContent>
      <ModalHeaderIcon>
        <img src={EvolutionIcon} alt="Evolução" width={45} height={39} />
        <ModalTitle>EVOLUÇÃO</ModalTitle>
      </ModalHeaderIcon>
      <ModalBody>
        <FormRow>
          <FormLabel>Plano Ação Produção:</FormLabel>
          <TimeField
            label="Horário Inicial"
            name="productionPlanTimeStart"
            value={form.productionPlanTimeStart}
            onChange={handleChange}
          />
          <TimeField
            label="Horário Final"
            name="productionPlanTimeEnd"
            value={form.productionPlanTimeEnd}
            onChange={handleChange}
          />
        </FormRow>
        <FormRow>
          <FormLabel>Plano Ação Contagem:</FormLabel>
          <TimeField
            label="Horário Inicial"
            name="countingPlanTimeStart"
            value={form.countingPlanTimeStart}
            onChange={handleChange}
          />
          <TimeField
            label="Horário Final"
            name="countingPlanTimeEnd"
            value={form.countingPlanTimeEnd}
            onChange={handleChange}
          />
        </FormRow>
        <FormRow>
          <FormLabel>Projeção Vendas:</FormLabel>
          <TimeField
            label="Horário Inicial"
            name="salesProjectionTimeStart"
            value={form.salesProjectionTimeStart}
            onChange={handleChange}
          />
          <TimeField
            label="Horário Final"
            name="salesProjectionTimeEnd"
            value={form.salesProjectionTimeEnd}
            onChange={handleChange}
          />
        </FormRow>
      </ModalBody>
      <ModalFooter>
        <Button
          variant="contained"
          design="contained"
          label="Cancelar"
          className="defaultButton backButton"
          onClick={onClose}
        />

        <Button
          variant="contained"
          design="contained"
          label="Salvar"
          className="defaultButton submitButton"
          onClick={handleSave}
        />
      </ModalFooter>
    </ModalContent>
  );
}

function Modal({ isOpen, onClose, type, data, onSave, restaurantId }) {
  if (!isOpen) return null;

  const renderContent = () => {
    switch (type) {
      case 'operations':
        return (
          <OperacaoContent
            data={data}
            onSave={onSave}
            onClose={onClose}
            restaurantId={restaurantId}
          />
        );
      case 'analyse':
        return <AnaliseContent data={data} onSave={onSave} onClose={onClose} />;
      case 'evolution':
        return (
          <EvolucaoContent data={data} onSave={onSave} onClose={onClose} />
        );
      default:
        return null;
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalWrapper onClick={(e) => e.stopPropagation()}>
        <ModalHeader>{renderContent()}</ModalHeader>
      </ModalWrapper>
    </ModalOverlay>
  );
}

export default Modal;
